// SostenibilidadFormatosTable.jsx
import { useState, useEffect, useRef } from "react";
import {
  Table, Button, Tag, Modal, Upload, Space,
  Typography, Card, message, Progress,
  Row, Col, Statistic, Breadcrumb,
  Alert,
} from "antd";
import {
  UploadOutlined, FileTextOutlined, CheckCircleFilled,
  ClockCircleFilled, InboxOutlined, ArrowLeftOutlined,
  EyeOutlined, HomeOutlined, DownloadOutlined,
  CheckOutlined, EditOutlined,
} from "@ant-design/icons";


// ─── Imports de formularios por formato ──────────────────────────────────────
import { getAllProyectosFormatos, getDescargarFormato, subirFormatoSostenibilidad } from "../../../services/sostenibilidad/formatos/sostenibilidadFormatos";
import ActaInicialPDF from "./actaInicio/PdfActaInicio";
import FormActaInicio from "./actaInicio/Formactainicio";
import ActaFinalPDF from "./actaFinalizacion/PdfActaFinal";
import FormActaFinalizacion from "./actaFinalizacion/FormActaFinalizacion";
import { useSelector } from 'react-redux';

// import FormActaSeguimiento from "./actaSeguimiento/FormActaSeguimiento";

/**
 * REGISTRO CENTRAL DE FORMATOS
 * ────────────────────────────────────────────────────────────────────────────
 * Clave  → sd_tipo_formato.codigo
 * pdf    → componente que renderiza el preview/descarga (forwardRef + descargarPDF)
 * form   → componente del formulario de edición
 *
 * Para agregar un nuevo formato:
 *   1. import NuevoPDF  from "./ruta/NuevoPDF";
 *   2. import NuevoForm from "./ruta/NuevoForm";
 *   3. Agregar entrada: NUEVO_CODIGO: { pdf: NuevoPDF, form: NuevoForm }
 */
const REGISTRO_FORMATOS = {
  ACTA_INICIO:       { pdf: ActaInicialPDF,  form: FormActaInicio },
  ACTA_FINALIZACION: { pdf: ActaFinalPDF,    form: FormActaFinalizacion  },
  // SEGUIMIENTO:  { pdf: ActaSeguimientoPDF, form: FormActaSeguimiento },
};


const { Text, Title } = Typography;
const { Dragger } = Upload;

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// ─── Modal de carga de archivo ────────────────────────────────────────────────
function ModalCargaArchivo({ open, formato, proyecto, onClose, onSuccess, username }) {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);

const handleUpload = async () => {
  if (!fileList.length) {
    message.warning("Selecciona un archivo antes de continuar");
    return;
  }
    if (!username) {
    message.error("No se puede cargar el archivo: usuario no identificado");
    return;
  }

  setUploading(true);
  try {
    const result = await subirFormatoSostenibilidad(
      proyecto.oid,
      proyecto.codigo_interno,   // <-- ajusta según cómo se llame el campo en tu objeto proyecto
      formato.oid_tipo_formato,
      username,
      fileList[0].originFileObj
    );

    message.success(result.mensaje ?? "Archivo cargado correctamente");
    onSuccess(proyecto.oid, formato.oid_tipo_formato, fileList[0].name);
    setFileList([]);
    onClose();

  } catch (error) {
    const msg = error?.response?.data?.mensaje ?? "Error al cargar el archivo";
    message.error(msg);
  } finally {
    setUploading(false);
  }
};

  return (
    <Modal
      open={open}
      onCancel={() => { setFileList([]); onClose(); }}
      title={<Space><UploadOutlined style={{ color: "#1677ff" }} /><span>Cargar documento — {formato?.nombre}</span></Space>}
      width={520}
      footer={[
        <Button key="cancel" onClick={() => { setFileList([]); onClose(); }}>Cancelar</Button>,
        <Button key="upload" type="primary" loading={uploading} disabled={!fileList.length} onClick={handleUpload} icon={<UploadOutlined />}>
          {uploading ? "Cargando..." : "Confirmar carga"}
        </Button>,
      ]}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: "100%", paddingTop: 8 }} size="middle">
        <Alert
          type="info" showIcon
          message={<><Text strong>{proyecto?.codigo_interno}</Text> · {proyecto?.nombre_profesional_gse}</>}
          description={`Vas a cargar el documento: ${formato?.nombre}`}
        />
        <Dragger
          fileList={fileList}
          beforeUpload={() => false}
          onChange={({ fileList: fl }) => setFileList(fl.slice(-1))}
          accept=".pdf,.doc,.docx,.xls,.xlsx"
          maxCount={1}
        >
          <p className="ant-upload-drag-icon"><InboxOutlined style={{ color: "#1677ff" }} /></p>
          <p className="ant-upload-text">Haz clic o arrastra el archivo aquí</p>
          <p className="ant-upload-hint">PDF, DOC, DOCX, XLS, XLSX · Máximo 1 archivo</p>
        </Dragger>
        {fileList.length > 0 && (
          <Alert type="success" showIcon icon={<CheckOutlined />}
            message={`Archivo seleccionado: ${fileList[0].name}`} />
        )}
      </Space>
    </Modal>
  );
}

// ─── Vista PDF (preview + descarga) ──────────────────────────────────────────
function VistaPDF({ proyecto, formato, onRegresar }) {
  const ComponentePDF = REGISTRO_FORMATOS[formato.codigo]?.pdf;
  const pdfRef        = useRef(null);
  const [descargando, setDescargando] = useState(false);

  const handleDescargar = async () => {
    if (!pdfRef.current?.descargarPDF) {
      message.warning("Este componente aún no expone descargarPDF");
      return;
    }
    setDescargando(true);
    try {
      await pdfRef.current.descargarPDF();
    } catch {
      message.error("Error al generar el PDF");
    } finally {
      setDescargando(false);
    }
  };

  return (
    <div style={{ padding: 24, background: "#f5f5f5", minHeight: "100vh" }}>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <><HomeOutlined /> Proyectos</>, onClick: onRegresar, href: "#" },
          { title: proyecto.codigo_interno },
          { title: formato.nombre },
        ]}
      />
      <Card style={{ marginBottom: 16 }}>
        <Row align="middle" gutter={16} wrap={false}>
          <Col flex="none">
            <Button icon={<ArrowLeftOutlined />} onClick={onRegresar}>Regresar</Button>
          </Col>
          <Col flex="auto">
            <Text strong style={{ fontSize: 15 }}>{formato.nombre}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 13 }}>
              {proyecto.codigo_interno} · {proyecto.nombre_profesional_gse} · {proyecto.proyecto_municipio}
            </Text>
          </Col>
          {ComponentePDF && (
            <Col flex="none">
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={descargando}
                onClick={handleDescargar}
              >
                {descargando ? "Generando PDF..." : "Descargar PDF"}
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {ComponentePDF
        ?<ComponentePDF ref={pdfRef} proyectoId={proyecto.oid} tipoFormatoID={formato.oid_tipo_formato} onClose={onRegresar} codigo_interno={proyecto.codigo_interno} />
        : (
          <Alert type="warning" showIcon
            message="Componente PDF no registrado"
            description={`Agrega el componente PDF para "${formato.codigo}" en REGISTRO_FORMATOS.`}
          />
        )
      }
    </div>
  );
}

// ─── Vista Formulario (edición) ───────────────────────────────────────────────
function VistaFormulario({ proyecto, formato, onRegresar, onUpdateDataLocal }) {
  const ComponenteForm = REGISTRO_FORMATOS[formato.codigo]?.form;

  return (
    <div style={{ padding: 24, background: "#f5f5f5", minHeight: "100vh" }}>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <><HomeOutlined /> Proyectos</>, onClick: onRegresar, href: "#" },
          { title: proyecto.codigo_interno },
          { title: `Editar — ${formato.nombre}` },
        ]}
      />
      <Card style={{ marginBottom: 16 }}>
        <Row align="middle" gutter={16} wrap={false}>
          <Col flex="none">
            <Button icon={<ArrowLeftOutlined />} onClick={onRegresar}>Regresar</Button>
          </Col>
          <Col flex="auto">
            <Text strong style={{ fontSize: 15 }}>Editar — {formato.nombre}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 13 }}>
              {proyecto.codigo_interno}
            </Text>
          </Col>
        </Row>
      </Card>

      {ComponenteForm
        ? <ComponenteForm proyectoId={proyecto.oid}  tipoFormatoID={formato.oid_tipo_formato}  onClose={onRegresar} updateDataLocal={onUpdateDataLocal }/>
        : (
          <Alert type="warning" showIcon
            message="Formulario no registrado"
            description={`Agrega el formulario para "${formato.codigo}" en REGISTRO_FORMATOS.`}
          />
        )
      }
    </div>
  );
}

// ─── Configuración visual por estado ─────────────────────────────────────────
const ESTADO_CONFIG = {
  SIN_DATOS:     { border: "#d9d9d9", bg: "#fafafa", tagColor: "default",  label: "SIN DATOS",     icon: <ClockCircleFilled style={{ fontSize: 22, color: "#bfbfbf" }} />, desc: "Sin datos — completa el formulario para comenzar" },
  PENDIENTE_PDF: { border: "#ffd591", bg: "#fffbe6", tagColor: "warning",  label: "PENDIENTE PDF", icon: <ClockCircleFilled style={{ fontSize: 22, color: "#faad14" }} />, desc: "Formulario guardado — descarga y carga el PDF firmado" },
  FINALIZADO:    { border: "#b7eb8f", bg: "#f6ffed", tagColor: "success",  label: "FINALIZADO",    icon: <CheckCircleFilled style={{ fontSize: 22, color: "#52c41a" }} />, desc: null },
};

// ─── Cards de formato dentro de la fila expandida ────────────────────────────
function FilaFormatos({ formatos, proyecto, onDescargar, onEditar, onCargar }) {
  return (
    <div style={{ padding: "8px 0", display: "flex", flexDirection: "column", gap: 10 }}>
      {formatos.map((fmt) => {
        const cfg = ESTADO_CONFIG[fmt.estado] ?? ESTADO_CONFIG.SIN_DATOS;

        return (
          <Card
            key={fmt.oid_tipo_formato}
            size="small"
            style={{ borderRadius: 8, border: `1px solid ${cfg.border}`, background: cfg.bg }}
            bodyStyle={{ padding: "12px 16px" }}
          >
            <Row align="middle" gutter={12} wrap={false}>

              {/* Ícono estado */}
              <Col flex="none">{cfg.icon}</Col>

              {/* Info */}
              <Col flex="auto" style={{ minWidth: 0 }}>
                <Text strong style={{ fontSize: 14, display: "block" }}>{fmt.nombre}</Text>
                {fmt.estado === "FINALIZADO"
                  ? <Text type="secondary" style={{ fontSize: 12 }}>
                      {fmt.nombre_archivo} · Cargado el {fmtDate(fmt.fecha_carga)} por {fmt.cargado_por}
                    </Text>
                  : <Text type={fmt.estado === "SIN_DATOS" ? "secondary" : "warning"} style={{ fontSize: 12 }}>
                      {cfg.desc}
                    </Text>
                }
              </Col>

              {/* Badge */}
              <Col flex="none">
                <Tag color={cfg.tagColor} style={{ fontWeight: 700, fontSize: 11 }}>
                  {cfg.label}
                </Tag>
              </Col>

              {/* Botonera según estado */}
              <Col flex="none" style={{ borderLeft: "1px solid #f0f0f0", paddingLeft: 12 }}>
                <Space size="small">

                  {/* SIN_DATOS → solo Editar (para llenar el formulario) */}
                  {fmt.estado === "SIN_DATOS" && (
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      size="small"
                      onClick={() => onEditar(proyecto, fmt)}
                    >
                      Editar
                    </Button>
                  )}

                  {/* PENDIENTE_PDF → Editar + Descargar formato + Cargar */}
                  {fmt.estado === "PENDIENTE_PDF" && (
                    <>
                      <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => onEditar(proyecto, fmt)}
                      >
                        Editar
                      </Button>
                      <Button
                        icon={<FileTextOutlined />}
                        size="small"
                        onClick={() => onDescargar(proyecto, fmt)}
                      >
                        Descargar formato
                      </Button>
                      <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        size="small"
                        onClick={() => onCargar(proyecto, fmt)}
                      >
                        Cargar PDF
                      </Button>
                    </>
                  )}

                  {/* FINALIZADO → solo Ver documento */}
                  {fmt.estado === "FINALIZADO" && (
                    <Button
                      type="primary"
                      icon={<EyeOutlined />}
                      size="small"
                      onClick={() =>
                         getDescargarFormato(proyecto.oid, fmt.oid_tipo_formato)
                          }         
                      >
                      Ver documento
                    </Button>
                  )}

                </Space>
              </Col>
            </Row>
          </Card>
        );
      })}
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function SostenibilidadFormatosTable() {
  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading]     = useState(true);
  // null | { tipo: "pdf" | "form", proyecto, formato }
  const [vistaActiva, setVistaActiva] = useState(null);
  const [modalCarga, setModalCarga] = useState({ open: false, formato: null, proyecto: null });
  const usernameFromState = useSelector((state) => state.auth.user);


  useEffect(() => {
  const fetchProyectos = async () => {
    try {
      setLoading(true);

      const response = await getAllProyectosFormatos();
      if(response.success){
          setProyectos(response?.data || {})
      }else{
          setProyectos(null)
      }

    } catch (error) {
      console.error("Error cargando proyectos:", error);
    } finally {
      setLoading(false);
    }
  };

  fetchProyectos();
}, []);


const actualizarEstadoFormatoForm = (proyectoOid, tipoFormatoId) => {
  setProyectos((prev) =>
    prev.map((proyecto) => {
      if (proyecto.oid !== proyectoOid) return proyecto;

      return {
        ...proyecto,
        formatos: proyecto.formatos?.map((formato) => {
          if (
            formato.oid_tipo_formato === tipoFormatoId &&
            formato.estado === "SIN_DATOS"
          ) {
            return {
              ...formato,
              estado: "PENDIENTE_PDF",
            };
          }

          return formato;
        }),
      };
    })
  );
};



  const handleUploadSuccess = (proyectoOid, tipoFormatoOid, fileName) => {
    setProyectos(prev => prev.map(p => {
      if (p.oid !== proyectoOid) return p;
      const formatos = p.formatos.map(f =>
        f.oid_tipo_formato !== tipoFormatoOid ? f : {
          ...f, estado: "FINALIZADO", nombre_archivo: fileName,
          fecha_carga: new Date().toISOString().split("T")[0], cargado_por: "usuario",
        }
      );
      const finalizados = formatos.filter(f => f.estado === "FINALIZADO").length;
      return { ...p, formatos, formatos_finalizados: finalizados, formatos_pendientes: formatos.length - finalizados };
    }));
  };

  const handleRegresar = () => setVistaActiva(null);

  // ─── Columnas ──────────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Código", dataIndex: "codigo_interno", width: 145,
      render: (v) => <Text code style={{ fontSize: 12 }}>{v}</Text>,
    },
    { title: "Implementador", dataIndex: "implementador",      ellipsis: true },
    { title: "Organización", dataIndex: "organizacion",      ellipsis: true },
    { title: "Departamento",     dataIndex: "departamento", width: 120 },
    { title: "Municipio",     dataIndex: "municipio", width: 120 },
    { title: "Grupo territorial",     dataIndex: "grupo_territorial", width: 120 },
    {
      title: "Progreso formatos", width: 200,
      render: (_, r) => {
        const pct    = r.total_formatos === 0 ? 0 : Math.round((r.formatos_finalizados / r.total_formatos) * 100);
        const status = pct === 100 ? "success" : pct > 0 ? "normal" : "exception";
        return (
          <Space direction="vertical" size={2} style={{ width: "100%" }}>
            <Progress percent={pct} size="small" status={status} style={{ margin: 0 }} />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {r.formatos_finalizados} de {r.total_formatos} finalizados
            </Text>
          </Space>
        );
      },
    },
  ];

  const expandedRowRender = (proyecto) => (
    <FilaFormatos
      formatos={proyecto.formatos}
      proyecto={proyecto}
      onDescargar={(p, f) => setVistaActiva({ tipo: "pdf",  proyecto: p, formato: f })}
      onEditar={   (p, f) => setVistaActiva({ tipo: "form", proyecto: p, formato: f })}
      onCargar={   (p, f) => setModalCarga({ open: true, formato: f, proyecto: p })}
    />
  );

  const stats = {
    total:     proyectos.length,
    completos: proyectos.filter(p => p.formatos_pendientes === 0).length,
    parciales: proyectos.filter(p => p.formatos_finalizados > 0 && p.formatos_pendientes > 0).length,
    pendientes:proyectos.filter(p => p.formatos_cargados === 0).length,
  };

  // ─── Vista PDF ─────────────────────────────────────────────────────────────
  if (vistaActiva?.tipo === "pdf") {
    return (
      <VistaPDF
        proyecto={vistaActiva.proyecto}
        formato={vistaActiva.formato}
        onRegresar={handleRegresar}
      />
    );
  }

  // ─── Vista Formulario ──────────────────────────────────────────────────────
  if (vistaActiva?.tipo === "form") {
    return (
      <VistaFormulario
        proyecto={vistaActiva.proyecto}
        formato={vistaActiva.formato}
        onRegresar={handleRegresar}
        onUpdateDataLocal= {actualizarEstadoFormatoForm}
        
      />
    );
  }

  // ─── Vista tabla principal ─────────────────────────────────────────────────
  return (
    <div style={{ padding: 24, background: "#f5f5f5", minHeight: "100vh" }}>
      <div style={{ marginBottom: 20 }}>
        <Title level={4} style={{ margin: 0 }}>Proyectos de Sostenibilidad</Title>
        <Text type="secondary">Gestión y seguimiento de formatos — expande una fila para gestionar sus documentos</Text>
      </div>

      <Row gutter={16} style={{ marginBottom: 20 }}>
        {[
          { label: "Total proyectos", value: stats.total,     color: "#1677ff" },
          { label: "Completos",       value: stats.completos,  color: "#52c41a" },
          { label: "Parciales",       value: stats.parciales,  color: "#faad14" },
          { label: "Sin formatos",    value: stats.pendientes, color: "#ff4d4f" },
        ].map((s) => (
          <Col xs={12} sm={6} key={s.label}>
            <Card size="small" style={{ textAlign: "center", borderTop: `3px solid ${s.color}` }}>
              <Statistic
                title={<span style={{ fontSize: 12 }}>{s.label}</span>}
                value={s.value}
                valueStyle={{ color: s.color, fontSize: 22, fontWeight: 700 }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          columns={columns}
          dataSource={proyectos}
          rowKey="oid"
          loading={loading}
          size="middle"
          expandable={{ expandedRowRender, rowExpandable: () => true }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} proyectos`,
          }}
        />
      </Card>

      <ModalCargaArchivo
        open={modalCarga.open}
        formato={modalCarga.formato}
        proyecto={modalCarga.proyecto}
        onClose={() => setModalCarga({ open: false, formato: null, proyecto: null })}
        onSuccess={handleUploadSuccess}
        username={usernameFromState.username}
      />
    </div>
  );
}